/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { addLocaleData, InjectedIntl, injectIntl, IntlProvider } from 'react-intl';
import deLocaleData from 'react-intl/locale-data/de';
import enLocaleData from 'react-intl/locale-data/en';
import esLocaleData from 'react-intl/locale-data/es';
import frLocaleData from 'react-intl/locale-data/fr';
import itLocaleData from 'react-intl/locale-data/it';
import jaLocaleData from 'react-intl/locale-data/ja';
import nlLocaleData from 'react-intl/locale-data/nl';
import zhHansLocaleData from 'react-intl/locale-data/zh';
import { getLocale } from 'src/assets/i18n/locales';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { Message } from 'src/core/types/Message';
import { LocalizationContext } from 'src/core/utils/contexts/LocalizationContext';

/**
 * Adds the supported locales to enable react-intl
 * to support the formatting of Dates, Numbers ...
 */
addLocaleData([
    ...enLocaleData,
    ...deLocaleData,
    ...esLocaleData,
    ...frLocaleData,
    ...itLocaleData,
    ...jaLocaleData,
    ...nlLocaleData,
    ...zhHansLocaleData
]);

export type LocalizationContextProviderProps = {
    /**
     * The injected intl object that is returned from injectIntl().
     */
    intl: InjectedIntl;

    /**
     * The projected elements passed from the consumer between
     * the dialog tags.
     */
    children: React.ReactNode;
};

export type LocalizationProviderProps = {
    /**
     * The current used locale.
     */
    locale: string;

    /**
     * The projected elements passed from the consumer between
     * the dialog tags.
     */
    children: React.ReactNode;
};

const LocalizationContextProvider = injectIntl((props: LocalizationContextProviderProps) => (
    <LocalizationContext.Provider value={props.intl}>{props.children}</LocalizationContext.Provider>
));

/**
 * The intl provider that wraps the application to support all the children components with InjectedIntl object.
 *
 * @param props is object of {
 *     locale: the current used locale in the application.
 *     children: the nested react components.
 * }
 */
export const LocalizationProvider = (props: LocalizationProviderProps) => {
    const { locale, children } = props;
    const [messages, setMessages] = React.useState<Message>(null);

    useAsyncEffect(async () => {
        if (locale) {
            const localeStrings = await getLocale(locale);
            setMessages(flattenMessages(localeStrings));
        }
    }, [locale]);

    return (
        <IntlProvider
            {...{
                key: locale,
                locale,
                messages
            }}>
            <LocalizationContextProvider>{children}</LocalizationContextProvider>
        </IntlProvider>
    );
};

/**
 * Flattens the messages if they were nested so if it was like:
 * {
 *      package: {
 *          component: {
 *              key: value
 *          }
 *      }
 * }
 * it would be like:
 * {
 *      'package.component.key': value
 * }
 *
 * @param nestedMessages The messages to be flattened.
 * @param prefix The prefix to be concatenated to the key if exists (as this function is recursive so this param is usually used to build
 * the complete path of the message).
 */
export const flattenMessages = (nestedMessages: Message, prefix = ''): Message => {
    const initial: Message = {};

    return Object.keys(nestedMessages).reduce((acc, key) => {
        const prefixedKey: string = prefix ? `${prefix}.${key}` : key;
        if (typeof nestedMessages[key] === 'string') {
            acc[prefixedKey] = nestedMessages[key];
        } else {
            Object.assign(acc, flattenMessages(nestedMessages[key] as Message, prefixedKey));
        }

        return acc;
    }, initial);
};
